/**
 * SOIL RECOMMENDATIONS (DEFERRED TRANSLATION)
 *
 * Instead of resolving translations at module import time (which freezes
 * the strings to whatever language was active during import), this module
 * returns translation keys and optional params. Consumers should call
 * `t(key, params)` at render time so values update immediately when the
 * language changes.
 */

// Helper to handle missing values consistently. Returns keys + params
const unavailable = (nutrient) => ({
  code: null,
  statusKey: "soilRecommendations.unavailable",
  recommendationKey: "soilRecommendations.unavailable",
  statusParams: { nutrient },
  recommendationParams: { nutrient },
});

/**
 * ─────────────────────────────────────────────
 * pH RECOMMENDATION
 * ─────────────────────────────────────────────
 */
const getPhRecommendation = (ph) => {
  if (ph === null || ph === undefined) return unavailable("pH");

  if (ph < 5.1) {
    return {
      code: "veryLow",
      statusKey: "soilRecommendations.pH.veryStronglyAcidic.status",
      recommendationKey: "soilRecommendations.pH.veryStronglyAcidic.recommendation",
    };
  }

  if (ph >= 5.1 && ph <= 5.5) {
    return {
      code: "low",
      statusKey: "soilRecommendations.pH.stronglyAcidic.status",
      recommendationKey: "soilRecommendations.pH.stronglyAcidic.recommendation",
    };
  }

  if (ph > 5.5 && ph <= 6.0) {
    return {
      code: "low",
      statusKey: "soilRecommendations.pH.moderatelyAcidic.status",
      recommendationKey: "soilRecommendations.pH.moderatelyAcidic.recommendation",
    };
  }

  if (ph > 6.0 && ph <= 6.5) {
    return {
      code: "medium",
      statusKey: "soilRecommendations.pH.slightlyAcidic.status",
      recommendationKey: "soilRecommendations.pH.slightlyAcidic.recommendation",
    };
  }

  if (ph > 6.5 && ph <= 7.3) {
    return {
      code: "medium",
      statusKey: "soilRecommendations.pH.neutral.status",
      recommendationKey: "soilRecommendations.pH.neutral.recommendation",
    };
  }

  if (ph > 7.3) {
    return {
      code: "high",
      statusKey: "soilRecommendations.pH.alkaline.status",
      recommendationKey: "soilRecommendations.pH.alkaline.recommendation",
    };
  }
};

/**
 * ─────────────────────────────────────────────
 * SOIL ORGANIC CARBON (SOC) RECOMMENDATION
 * ─────────────────────────────────────────────
 */
const getSocRecommendation = (soc) => {
  if (soc === null || soc === undefined) return unavailable("SOC");

  if (soc >= 0 && soc < 5) {
    return {
      code: "veryLow",
      statusKey: "soilRecommendations.SOC.veryLow.status",
      recommendationKey: "soilRecommendations.SOC.veryLow.recommendation",
    };
  }

  if (soc >= 5 && soc < 10) {
    return {
      code: "low",
      statusKey: "soilRecommendations.SOC.low.status",
      recommendationKey: "soilRecommendations.SOC.low.recommendation",
    };
  }

  if (soc >= 10 && soc <= 15) {
    return {
      code: "medium",
      statusKey: "soilRecommendations.SOC.medium.status",
      recommendationKey: "soilRecommendations.SOC.medium.recommendation",
    };
  }

  if (soc > 15) {
    return {
      code: "high",
      statusKey: "soilRecommendations.SOC.high.status",
      recommendationKey: "soilRecommendations.SOC.high.recommendation",
    };
  }
};

/**
 * ─────────────────────────────────────────────
 * NITROGEN RECOMMENDATION
 * ─────────────────────────────────────────────
 */
const getNitrogenRecommendation = (nitrogen) => {
  if (nitrogen === null || nitrogen === undefined) return unavailable("Nitrogen");

  if (nitrogen >= 0 && nitrogen < 0.5) {
    return {
      code: "veryLow",
      statusKey: "soilRecommendations.nitrogen.veryLow.status",
      recommendationKey: "soilRecommendations.nitrogen.veryLow.recommendation",
    };
  }

  if (nitrogen >= 0.5 && nitrogen <= 1.5) {
    return {
      code: "low",
      statusKey: "soilRecommendations.nitrogen.low.status",
      recommendationKey: "soilRecommendations.nitrogen.low.recommendation",
    };
  }

  if (nitrogen > 1.5) {
    return {
      code: "high",
      statusKey: "soilRecommendations.nitrogen.adequate.status",
      recommendationKey: "soilRecommendations.nitrogen.adequate.recommendation",
    };
  }
};

/**
 * ─────────────────────────────────────────────
 * PHOSPHORUS RECOMMENDATION
 * ─────────────────────────────────────────────
 */
const getPhosphorusRecommendation = (phosphorus) => {
  if (phosphorus === null || phosphorus === undefined) return unavailable("Phosphorus");

  if (phosphorus >= 0 && phosphorus < 6) {
    return {
      code: "veryLow",
      statusKey: "soilRecommendations.phosphorus.veryLow.status",
      recommendationKey: "soilRecommendations.phosphorus.veryLow.recommendation",
    };
  }

  if (phosphorus >= 6 && phosphorus < 10) {
    return {
      code: "low",
      statusKey: "soilRecommendations.phosphorus.low.status",
      recommendationKey: "soilRecommendations.phosphorus.low.recommendation",
    };
  }

  if (phosphorus >= 10 && phosphorus < 20) {
    return {
      code: "medium",
      statusKey: "soilRecommendations.phosphorus.medium.status",
      recommendationKey: "soilRecommendations.phosphorus.medium.recommendation",
    };
  }

  if (phosphorus >= 20) {
    return {
      code: "high",
      statusKey: "soilRecommendations.phosphorus.high.status",
      recommendationKey: "soilRecommendations.phosphorus.high.recommendation",
    };
  }
};

/**
 * ─────────────────────────────────────────────
 * POTASSIUM RECOMMENDATION
 * ─────────────────────────────────────────────
 */
const getPotassiumRecommendation = (potassium) => {
  if (potassium === null || potassium === undefined) return unavailable("Potassium");

  if (potassium >= 0 && potassium < 0.09) {
    return {
      code: "veryLow",
      statusKey: "soilRecommendations.potassium.veryLow.status",
      recommendationKey: "soilRecommendations.potassium.veryLow.recommendation",
    };
  }

  if (potassium >= 0.09 && potassium < 0.15) {
    return {
      code: "low",
      statusKey: "soilRecommendations.potassium.low.status",
      recommendationKey: "soilRecommendations.potassium.low.recommendation",
    };
  }

  if (potassium >= 0.15 && potassium < 0.3) {
    return {
      code: "medium",
      statusKey: "soilRecommendations.potassium.medium.status",
      recommendationKey: "soilRecommendations.potassium.medium.recommendation",
    };
  }

  if (potassium >= 0.3) {
    return {
      code: "high",
      statusKey: "soilRecommendations.potassium.high.status",
      recommendationKey: "soilRecommendations.potassium.high.recommendation",
    };
  }
};

/**
 * ─────────────────────────────────────────────
 * COMBINED RECOMMENDATION
 * Runs all recommendation functions at once
 * Returns single object with all results
 * ─────────────────────────────────────────────
 */
export const getCombinedRecommendation = (ph, soc, nitrogen, phosphorus, potassium) => ({
  ph: getPhRecommendation(ph),
  soc: getSocRecommendation(soc),
  nitrogen: getNitrogenRecommendation(nitrogen),
  phosphorus: getPhosphorusRecommendation(phosphorus),
  potassium: getPotassiumRecommendation(potassium),
});

export default getCombinedRecommendation;
